import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useAuth0 } from '@auth0/auth0-react';

import { useLayout } from '~hooks/useLayout';
import { useSeo } from '~hooks/useSeo';
import { UserContext } from '~contexts/user';
import * as REST from '~services/rest';
import useStripe from '../useStripe';

import './style.scss';

const PaymentProcess = () => {
  const intl = useIntl();
  const history = useHistory();
  const { user } = useContext(UserContext);
  const { getAccessTokenSilently } = useAuth0();
  const { retriveCheckoutSession } = useStripe();

  const [contactId, setContactId] = useState(null);
  const [paymentState, setPaymentState] = useState('checking');
  const [accessState, setAccessState] = useState('checking');

  useLayout({ displayCities: false });
  useSeo(intl.formatMessage({ id: 'containers.payment.subscription_process.title' }));

  // The Stripe Checkout return alone never authorizes a chat: the backend must
  // persist the payment and grant access for the selected helper first.
  useEffect(() => {
    let cancelled = false;
    const confirmCheckout = async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('session_id');
      const selectedContactId = params.get('contactId');
      setContactId(selectedContactId);

      if (!sessionId || !selectedContactId) {
        setPaymentState('unconfirmed');
        return;
      }
      try {
        const result = await retriveCheckoutSession(sessionId);
        if (cancelled) return;
        setPaymentState(result?.data?.payment_status === 'paid' ? 'paid' : 'unconfirmed');
      } catch (error) {
        if (!cancelled) setPaymentState('unconfirmed');
      }
    };
    confirmCheckout();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (paymentState !== 'paid' || !contactId || !user?.personId) return undefined;

    let cancelled = false;
    let timer;
    let attempts = 0;
    const checkEntitlement = async () => {
      attempts += 1;
      try {
        const token = await getAccessTokenSilently();
        const response = await REST.get({
          name: 'payments',
          requiresAuthentication: true,
          shouldThrowError: true,
          mockFileName: 'payments/can-chat',
          params: { 'customer-id': user.personId, 'worker-id': contactId },
          token,
        });
        if (cancelled) return;
        if (response?.data?.allowed_to_talk === true) {
          history.replace(`/thank-you?returnUrl=/discussion/${contactId}&isChat=true`);
          return;
        }
        if (attempts >= 8) {
          setAccessState(response?.data?.reason === 'wrong_location' ? 'wrong_location' : 'pending');
          return;
        }
      } catch (error) {
        if (cancelled) return;
        if (attempts >= 8) {
          setAccessState('pending');
          return;
        }
      }
      timer = setTimeout(checkEntitlement, 1200);
    };
    checkEntitlement();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [paymentState, contactId, user?.personId, getAccessTokenSilently, history]);

  const message = (id, defaultMessage) => intl.formatMessage({
    id: `containers.payment.subscription_process.${id}`,
    defaultMessage,
  });

  return (
    <div className="payment-process">
      {paymentState === 'checking' && (
        <p>{message('retrive', 'Retrieving your payment information...')}</p>
      )}
      {paymentState === 'unconfirmed' && (
        <p>{message(
          'payment_unconfirmed',
          'We could not confirm the payment yet. Please do not pay again. Check your subscription or contact support.'
        )}</p>
      )}
      {paymentState === 'paid' && (
        <div>
          <p>{message('payment_successfull', 'Payment successful.')}</p>
          {accessState === 'wrong_location' ? (
            <p>{message(
              'address_mismatch',
              'Your payment was received, but the paid address does not match this helper\'s service area. Do not pay again. Contact support to verify the address.'
            )}</p>
          ) : accessState === 'pending' ? (
            <p>{message(
              'access_pending',
              'Your payment was received, but chat access could not be confirmed. Do not pay again. Please retry later or contact support.'
            )}</p>
          ) : (
            <p>{message('checking_access', 'We are checking your access to this conversation.')}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentProcess;
